import { useCallback, type FormEvent, type KeyboardEvent } from 'react';
import { GlobalButton } from './GlobalButton';
import { GlobalTextarea } from './GlobalTextarea';
import styles from './ChatComposer.module.css';

export type ChatComposerProps = {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
  loading?: boolean;
  placeholder?: string;
};

export function ChatComposer({
  value,
  onChange,
  onSend,
  loading = false,
  placeholder = 'Ask about your portfolio, net worth, or bank activity…',
}: ChatComposerProps) {
  const trimmed = value.trim();
  const canSend = trimmed.length > 0 && !loading;

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (!canSend) return;
      onSend();
    },
    [canSend, onSend]
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.key !== 'Enter' || event.shiftKey) return;
      event.preventDefault();
      if (!canSend) return;
      onSend();
    },
    [canSend, onSend]
  );

  return (
    <form className={styles.composer} onSubmit={handleSubmit} aria-label="Chat message composer">
      <GlobalTextarea
        label="Message"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={handleKeyDown}
        disabled={loading}
        rows={2}
        className={styles.textarea}
        placeholder={placeholder}
        aria-label="Message"
      />

      <div className={styles.actions}>
        <p className={`${styles.hint} muted`}>Shift+Enter for a new line</p>
        <GlobalButton
          type="submit"
          variant="primary"
          autoWidth
          className={styles.sendButton}
          disabled={!canSend}
          isLoading={loading}
        >
          Send
        </GlobalButton>
      </div>
    </form>
  );
}
